// Context Map API — structured exploration result for LLM tool consumption (#206).
//
// Recomposes existing impact analysis, ranking, and context-pack assembly into
// a graph-shaped response with suggested read/edit/validation targets.

import { readFile } from "node:fs/promises";
import { join } from "node:path";

import type { Evidence } from "../evidence/types.js";
import type { DependencyGraph } from "../dependency/types.js";
import type { AffectedEntry, ImpactReport } from "../impact/types.js";
import { analyzeImpact } from "../impact/index.js";
import type { EdgeClassification } from "../types.js";
import type { EvidenceType } from "../evidence/types.js";
import { rankFiles, type RankedFile } from "./ranker.js";
import { selectWithinBudget } from "./budget.js";
import { estimateTokens } from "./budget.js";
import type { ContentMode } from "./types.js";
import type { GraphEdge } from "../types.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ContextMapCandidate {
  path?: string;
  symbolId?: string;
  artifactId?: string;
}

export type ContextMapDirection = "downstream" | "upstream" | "both";

export interface GetContextMapInput {
  instruction: string;
  candidates: ContextMapCandidate[];
  depth?: number;
  direction?: ContextMapDirection;
  budgetTokens?: number;
  include?: Array<
    | "dependencies"
    | "reverseDependencies"
    | "artifacts"
    | "symbols"
    | "validations"
    | "workflows"
  >;
}

export interface ContextRoot {
  id: string;
  kind: "file" | "symbol" | "artifact";
  path?: string;
  symbolId?: string;
  artifactId?: string;
}

export interface ContextMapNode {
  id: string;
  kind: "file" | "symbol" | "artifact" | "validator" | "workflow";
  label: string;
  path?: string;
  symbolId?: string;
  artifactId?: string;
  relevance: number;
}

export type ContextMapEdgeType =
  | "imports"
  | "calls"
  | "contains"
  | "belongs_to_artifact"
  | "produces"
  | "consumes"
  | "validated_by"
  | "workflow_uses";

export interface ContextMapEdge {
  from: string;
  to: string;
  type: ContextMapEdgeType;
  confidence: number;
  evidence: Evidence[];
}

export interface ContextMapTarget {
  path?: string;
  symbolId?: string;
  artifactId?: string;
  reason: string;
  contentMode?: ContentMode;
  relevance?: number;
}

export interface ContextMap {
  roots: ContextRoot[];
  nodes: ContextMapNode[];
  edges: ContextMapEdge[];
  suggestedReadTargets: ContextMapTarget[];
  suggestedEditTargets: ContextMapTarget[];
  suggestedValidationTargets: ContextMapTarget[];
}

export interface GetContextMapOptions extends GetContextMapInput {
  graph: DependencyGraph;
  projectRoot: string;
  edgeTypes?: EdgeClassification[];
  evidenceExclude?: EvidenceType[];
  limit?: number;
  navigationIndex?: Record<string, unknown>;
  fileContents?: Map<string, string>;
}

const DEFAULT_BUDGET_TOKENS = 50_000;

const CONTENT_MODE_THRESHOLDS = {
  full: 0.7,
  section: 0.4,
  signature: 0.2,
  fullMaxTokens: 2000,
} as const;

function selectContentMode(relevanceScore: number, tokens: number): ContentMode {
  if (
    relevanceScore >= CONTENT_MODE_THRESHOLDS.full &&
    tokens <= CONTENT_MODE_THRESHOLDS.fullMaxTokens
  ) {
    return "full";
  }
  if (relevanceScore >= CONTENT_MODE_THRESHOLDS.section) return "section";
  if (relevanceScore >= CONTENT_MODE_THRESHOLDS.signature) return "signature";
  return "reference";
}

// ---------------------------------------------------------------------------
// Candidate resolution
// ---------------------------------------------------------------------------

export function resolveContextMapSeeds(
  graph: DependencyGraph,
  candidates: ContextMapCandidate[],
): { seeds: string[]; roots: ContextRoot[] } {
  const seeds: string[] = [];
  const roots: ContextRoot[] = [];
  const seen = new Set<string>();

  for (const candidate of candidates) {
    if (candidate.symbolId) {
      if (!seen.has(candidate.symbolId)) {
        seen.add(candidate.symbolId);
        seeds.push(candidate.symbolId);
        roots.push({
          id: candidate.symbolId,
          kind: "symbol",
          symbolId: candidate.symbolId,
          path: candidate.symbolId.split("#")[0],
        });
      }
      continue;
    }

    if (candidate.artifactId) {
      const files = filesForArtifact(graph, candidate.artifactId);
      roots.push({
        id: candidate.artifactId,
        kind: "artifact",
        artifactId: candidate.artifactId,
      });
      for (const path of files) {
        if (!seen.has(path)) {
          seen.add(path);
          seeds.push(path);
        }
      }
      continue;
    }

    if (candidate.path) {
      if (!seen.has(candidate.path)) {
        seen.add(candidate.path);
        seeds.push(candidate.path);
        roots.push({
          id: candidate.path,
          kind: "file",
          path: candidate.path,
        });
      }
    }
  }

  return { seeds, roots };
}

function filesForArtifact(graph: DependencyGraph, artifactId: string): string[] {
  const files: string[] = [];
  for (const [path, ids] of graph.artifactMapping) {
    if (ids.includes(artifactId)) files.push(path);
  }
  return files;
}

// ---------------------------------------------------------------------------
// Impact merge (both directions)
// ---------------------------------------------------------------------------

async function runMergedImpact(
  options: GetContextMapOptions,
  changed: string[],
): Promise<ImpactReport> {
  const {
    graph,
    direction = "both",
    depth,
    edgeTypes,
    evidenceExclude,
    limit,
    navigationIndex,
  } = options;
  const artifactMapping = graph.artifactMapping;

  const baseOpts = {
    graph,
    changed,
    ...(depth !== undefined ? { maxDepth: depth } : {}),
    ...(edgeTypes ? { edgeTypes } : {}),
    ...(evidenceExclude ? { evidenceExclude } : {}),
    ...(limit !== undefined ? { limit } : {}),
    artifactMapping,
    ...(navigationIndex ? { navigationIndex } : {}),
  };

  if (direction !== "both") {
    return analyzeImpact({ ...baseOpts, direction });
  }

  const downstream = await analyzeImpact({ ...baseOpts, direction: "downstream" });
  const upstream = await analyzeImpact({ ...baseOpts, direction: "upstream" });

  const mergeEntries = (
    a: AffectedEntry[],
    b: AffectedEntry[],
  ): AffectedEntry[] => {
    const byKey = new Map<string, AffectedEntry>();
    for (const entry of [...a, ...b]) {
      const key = entry.symbolId ?? entry.path;
      const existing = byKey.get(key);
      if (!existing || entry.depth < existing.depth) {
        byKey.set(key, entry);
      }
    }
    return [...byKey.values()];
  };

  return {
    ...downstream,
    direction: "downstream",
    directlyAffected: mergeEntries(
      downstream.directlyAffected,
      upstream.directlyAffected,
    ),
    transitivelyAffected: mergeEntries(
      downstream.transitivelyAffected,
      upstream.transitivelyAffected,
    ),
    affectedArtifacts: [
      ...new Map(
        [...downstream.affectedArtifacts, ...upstream.affectedArtifacts].map(
          (a) => [a.artifactId, a],
        ),
      ).values(),
    ],
    affectedValidations: [
      ...new Set([
        ...downstream.affectedValidations,
        ...upstream.affectedValidations,
      ]),
    ],
    affectedAgents: [
      ...new Set([...downstream.affectedAgents, ...upstream.affectedAgents]),
    ],
    affectedWorkflows: [
      ...new Set([...downstream.affectedWorkflows, ...upstream.affectedWorkflows]),
    ],
    testMapping: [
      ...new Map(
        [...downstream.testMapping, ...upstream.testMapping].map((t) => [
          `${t.sourcePath}:${t.testPaths.join(",")}`,
          t,
        ]),
      ).values(),
    ],
    maxConfidence: Math.max(downstream.maxConfidence, upstream.maxConfidence),
  };
}

// ---------------------------------------------------------------------------
// Graph assembly
// ---------------------------------------------------------------------------

function mapEdgeKindToType(kind: string): ContextMapEdgeType {
  if (kind === "call" || kind === "calls") return "calls";
  if (kind === "import" || kind === "imports" || kind === "static_import") {
    return "imports";
  }
  if (kind === "produces") return "produces";
  if (kind === "consumes") return "consumes";
  if (kind === "validated_by") return "validated_by";
  if (kind === "workflow_uses") return "workflow_uses";
  if (kind === "contains") return "contains";
  return "imports";
}

function buildNodesAndEdges(
  report: ImpactReport,
  ranked: RankedFile[],
  graph: DependencyGraph,
  roots: ContextRoot[],
): { nodes: ContextMapNode[]; edges: ContextMapEdge[] } {
  const nodes = new Map<string, ContextMapNode>();
  const edges: ContextMapEdge[] = [];
  const edgeKeys = new Set<string>();
  const affectedPaths = new Set(
    [
      ...report.directlyAffected,
      ...report.transitivelyAffected,
    ].map((e) => e.path),
  );

  const addNode = (node: ContextMapNode): void => {
    const existing = nodes.get(node.id);
    if (!existing || node.relevance > existing.relevance) {
      nodes.set(node.id, node);
    }
  };

  const addGraphEdge = (edge: GraphEdge): void => {
    const edgeType = mapEdgeKindToType(edge.kind);
    const key = `${edge.from}|${edge.to}|${edgeType}`;
    if (edgeKeys.has(key)) return;
    edgeKeys.add(key);
    const confidence =
      edge.relevanceScore ??
      (edge.evidence.length > 0
        ? Math.max(...edge.evidence.map((e) => e.confidence))
        : 1);
    edges.push({
      from: edge.from,
      to: edge.to,
      type: edgeType,
      confidence,
      evidence: edge.evidence,
    });
  };

  for (const root of roots) {
    addNode({
      id: root.id,
      kind: root.kind === "artifact" ? "artifact" : root.kind,
      label: root.artifactId ?? root.symbolId ?? root.path ?? root.id,
      path: root.path,
      symbolId: root.symbolId,
      artifactId: root.artifactId,
      relevance: 1,
    });
  }

  const allEntries = [
    ...report.directlyAffected,
    ...report.transitivelyAffected,
  ];

  for (const entry of allEntries) {
    const nodeId = entry.symbolId ?? entry.path;
    addNode({
      id: nodeId,
      kind: entry.symbolId ? "symbol" : "file",
      label: entry.symbolName ?? entry.path,
      path: entry.path,
      symbolId: entry.symbolId,
      artifactId: entry.artifactId,
      relevance: entry.relevanceScore ?? Math.max(0, 1 - entry.depth * 0.15),
    });

    const artifactIds = graph.artifactMapping.get(entry.path) ?? [];
    for (const artifactId of artifactIds) {
      const key = `${entry.path}|${artifactId}|belongs_to_artifact`;
      if (edgeKeys.has(key)) continue;
      edgeKeys.add(key);
      edges.push({
        from: entry.path,
        to: artifactId,
        type: "belongs_to_artifact",
        confidence: 1,
        evidence: [],
      });
      addNode({
        id: artifactId,
        kind: "artifact",
        label: artifactId,
        artifactId,
        relevance: entry.relevanceScore ?? 0.8,
      });
    }
  }

  for (const rankedFile of ranked) {
    addNode({
      id: rankedFile.path,
      kind: "file",
      label: rankedFile.path,
      path: rankedFile.path,
      artifactId: rankedFile.artifactId,
      relevance: rankedFile.relevanceScore,
    });
  }

  for (const validation of report.affectedValidations) {
    addNode({
      id: validation,
      kind: "validator",
      label: validation,
      relevance: 0.9,
    });
  }

  for (const workflow of report.affectedWorkflows) {
    addNode({
      id: workflow,
      kind: "workflow",
      label: workflow,
      relevance: 0.85,
    });
  }

  for (const edge of graph.edges) {
    if (affectedPaths.has(edge.from) || affectedPaths.has(edge.to)) {
      addGraphEdge(edge);
    }
  }

  return { nodes: [...nodes.values()], edges };
}

function buildSuggestedTargets(
  report: ImpactReport,
  ranked: RankedFile[],
  fileContents: Map<string, string>,
  seeds: string[],
): {
  suggestedReadTargets: ContextMapTarget[];
  suggestedEditTargets: ContextMapTarget[];
  suggestedValidationTargets: ContextMapTarget[];
} {
  const suggestedReadTargets: ContextMapTarget[] = ranked.map((file) => {
    const content = fileContents.get(file.path) ?? "";
    const tokens = estimateTokens(content);
    const contentMode = selectContentMode(file.relevanceScore, tokens);
    const reason =
      file.depth === 0
        ? "directly changed or candidate seed"
        : `affected at depth ${file.depth}`;
    return {
      path: file.path,
      artifactId: file.artifactId,
      reason,
      contentMode,
      relevance: file.relevanceScore,
    };
  });

  const seedSet = new Set(seeds);
  const suggestedEditTargets: ContextMapTarget[] = seeds.map((seed) => ({
    path: seed.includes("#") ? seed.split("#")[0] : seed,
    symbolId: seed.includes("#") ? seed : undefined,
    reason: "candidate seed from exploration",
    relevance: 1,
  }));

  const suggestedValidationTargets: ContextMapTarget[] = [];
  for (const validation of report.affectedValidations) {
    suggestedValidationTargets.push({
      artifactId: validation,
      reason: "contract validation implicated by change",
      relevance: 0.9,
    });
  }
  for (const test of report.testMapping) {
    for (const testPath of test.testPaths) {
      suggestedValidationTargets.push({
        path: testPath,
        reason: `test for ${test.sourcePath} (${test.method})`,
        relevance: 0.8,
      });
    }
  }

  return {
    suggestedReadTargets,
    suggestedEditTargets,
    suggestedValidationTargets,
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export async function getContextMap(
  options: GetContextMapOptions,
): Promise<ContextMap> {
  const {
    graph,
    candidates,
    instruction: _instruction,
    budgetTokens = DEFAULT_BUDGET_TOKENS,
    projectRoot,
    fileContents: preloadedContents,
    navigationIndex,
    edgeTypes,
    evidenceExclude,
    limit,
  } = options;

  if (candidates.length === 0) {
    throw new Error("getContextMap requires at least one candidate.");
  }

  const { seeds, roots } = resolveContextMapSeeds(graph, candidates);
  if (seeds.length === 0) {
    throw new Error("No resolvable seeds from candidates.");
  }

  const report = await runMergedImpact(options, seeds);

  const seen = new Set<string>();
  const affectedEntries: AffectedEntry[] = [];
  for (const path of seeds) {
    if (seen.has(path)) continue;
    seen.add(path);
    const artifactIds = graph.artifactMapping.get(
      path.includes("#") ? path.split("#")[0] : path,
    );
    affectedEntries.push({
      path: path.includes("#") ? path.split("#")[0] : path,
      ...(path.includes("#") ? { symbolId: path } : {}),
      ...(artifactIds?.[0] ? { artifactId: artifactIds[0] } : {}),
      depth: 0,
      evidence: [],
    });
  }
  for (const entry of [
    ...report.directlyAffected,
    ...report.transitivelyAffected,
  ]) {
    const key = entry.symbolId ?? entry.path;
    if (seen.has(key)) continue;
    seen.add(key);
    affectedEntries.push(entry);
  }

  const edgeCounts = new Map<string, number>();
  for (const edge of graph.edges) {
    edgeCounts.set(edge.from, (edgeCounts.get(edge.from) ?? 0) + 1);
    edgeCounts.set(edge.to, (edgeCounts.get(edge.to) ?? 0) + 1);
  }

  const rankedAll = rankFiles({
    affectedEntries,
    artifactMapping: graph.artifactMapping,
    edgeCounts,
    ...(evidenceExclude ? { evidenceExclude } : {}),
  });
  const ranked =
    limit !== undefined && limit >= 0
      ? rankedAll.slice(0, limit)
      : rankedAll;

  const fileContents = new Map<string, string>(preloadedContents ?? []);
  await Promise.all(
    ranked.map(async (file) => {
      if (fileContents.has(file.path)) return;
      try {
        const content = await readFile(join(projectRoot, file.path), "utf8");
        fileContents.set(file.path, content);
      } catch {
        fileContents.set(file.path, "");
      }
    }),
  );

  const selection = selectWithinBudget({ ranked, budgetTokens, fileContents });
  const budgetRanked = ranked.filter((file) =>
    selection.selected.some((s) => s.path === file.path),
  );

  const { nodes, edges } = buildNodesAndEdges(report, budgetRanked, graph, roots);
  const {
    suggestedReadTargets,
    suggestedEditTargets,
    suggestedValidationTargets,
  } = buildSuggestedTargets(report, budgetRanked, fileContents, seeds);

  return {
    roots,
    nodes,
    edges,
    suggestedReadTargets,
    suggestedEditTargets,
    suggestedValidationTargets,
  };
}
